import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { projectAPI, taskAPI, expenseAPI, photoAPI, compAPI, aiAPI, permitAPI, contractorPortalAPI, contractorAccountAPI, reportAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import DocumentVault from '../../components/DocumentVault';
import { saveAs } from 'file-saver';
import { 
  ArrowLeft, 
  Building2, 
  MapPin, 
  DollarSign, 
  Clock, 
  CheckCircle2,
  Plus,
  Trash2,
  Edit2,
  Camera,
  TrendingUp,
  Brain,
  Loader2,
  AlertTriangle,
  Calendar,
  User,
  FileText,
  Link as LinkIcon,
  Copy,
  HardHat,
  Mail,
  Phone,
  Key,
  Eye,
  Upload,
  RefreshCw,
  Download,
  Share2
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'Not set';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [comps, setComps] = useState([]);
  const [permits, setPermits] = useState([]);
  const [portals, setPortals] = useState([]);
  const [contractorAccounts, setContractorAccounts] = useState([]);
  const [contractorUploads, setContractorUploads] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Dialog states
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [compDialogOpen, setCompDialogOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [editStatusDialogOpen, setEditStatusDialogOpen] = useState(false);
  const [permitDialogOpen, setPermitDialogOpen] = useState(false);
  const [portalDialogOpen, setPortalDialogOpen] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);

  // Form states
  const [newTask, setNewTask] = useState({ title: '', description: '', contractor_name: '', estimated_cost: '', due_date: '', priority: 'medium' });
  const [newExpense, setNewExpense] = useState({ category: '', description: '', amount: '', vendor: '', date: '' });
  const [newComp, setNewComp] = useState({ address: '', city: '', state: '', zipcode: '', sale_price: '', sale_date: '', beds: '', baths: '', sqft: '', notes: '' });
  const [newPermit, setNewPermit] = useState({ permit_type: '', permit_number: '', status: 'pending', issue_date: '', expiration_date: '', fee: '', inspection_date: '', inspection_status: '', inspection_notes: '', notes: '' });
  const [newPortal, setNewPortal] = useState({ contractor_name: '', expires_in_days: 30 });
  const [newAccount, setNewAccount] = useState({ name: '', email: '', phone: '', company: '', specialty: '' });
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoCategory, setPhotoCategory] = useState('before');
  const [photoDescription, setPhotoDescription] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiQuestion, setAiQuestion] = useState('');
  const [createdPortal, setCreatedPortal] = useState(null);
  const [createdAccount, setCreatedAccount] = useState(null);
  const [suggestedComps, setSuggestedComps] = useState([]);
  const [compsGenerating, setCompsGenerating] = useState(false);
  const [editingComp, setEditingComp] = useState(null);

  useEffect(() => {
    fetchProjectData();
  }, [id]);

  const fetchProjectData = async () => {
    try {
      const [projectRes, statsRes, tasksRes, expensesRes, photosRes, compsRes, permitsRes, portalsRes, accountsRes, uploadsRes] = await Promise.all([
        projectAPI.getOne(id),
        projectAPI.getStats(id),
        taskAPI.getAll(id),
        expenseAPI.getAll(id),
        photoAPI.getAll(id),
        compAPI.getAll(id),
        permitAPI.getAll(id),
        contractorPortalAPI.getAll(id),
        contractorAccountAPI.getAll(id),
        contractorPortalAPI.getUploads(id)
      ]);
      setProject(projectRes.data);
      setStats(statsRes.data);
      setTasks(tasksRes.data);
      setExpenses(expensesRes.data);
      setPhotos(photosRes.data);
      setComps(compsRes.data);
      setPermits(permitsRes.data);
      setPortals(portalsRes.data);
      setContractorAccounts(accountsRes.data);
      setContractorUploads(uploadsRes.data);
      setNewStatus(projectRes.data.status);
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title) {
      toast.error('Task title is required');
      return;
    }
    try {
      await taskAPI.create({
        project_id: id,
        ...newTask,
        estimated_cost: parseFloat(newTask.estimated_cost) || 0
      });
      toast.success('Task added');
      setTaskDialogOpen(false);
      setNewTask({ title: '', description: '', contractor_name: '', estimated_cost: '', due_date: '', priority: 'medium' });
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const handleUpdateTaskStatus = async (taskId, status) => {
    try {
      await taskAPI.update(taskId, { status });
      toast.success('Task updated');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await taskAPI.delete(taskId);
      toast.success('Task deleted');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleAddExpense = async () => {
    if (!newExpense.category || !newExpense.amount) {
      toast.error('Category and amount are required');
      return;
    }
    try {
      await expenseAPI.create({
        project_id: id,
        ...newExpense,
        amount: parseFloat(newExpense.amount) || 0
      });
      toast.success('Expense added');
      setExpenseDialogOpen(false);
      setNewExpense({ category: '', description: '', amount: '', vendor: '', date: '' });
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await expenseAPI.delete(expenseId);
      toast.success('Expense deleted');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleAddComp = async () => {
    if (!newComp.address || !newComp.sale_price) {
      toast.error('Address and sale price are required');
      return;
    }
    try {
      await compAPI.create({
        project_id: id,
        ...newComp,
        sale_price: parseFloat(newComp.sale_price) || 0,
        beds: parseInt(newComp.beds) || 0,
        baths: parseFloat(newComp.baths) || 0,
        sqft: parseInt(newComp.sqft) || 0
      });
      toast.success('Comp added');
      setCompDialogOpen(false);
      setNewComp({ address: '', city: '', state: '', zipcode: '', sale_price: '', sale_date: '', beds: '', baths: '', sqft: '', notes: '' });
      setEditingComp(null);
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to add comp');
    }
  };

  const handleDeleteComp = async (compId) => {
    try {
      await compAPI.delete(compId);
      toast.success('Comp deleted');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete comp');
    }
  };

  // AI Comps Generator
  const handleGenerateComps = async () => {
    setCompsGenerating(true);
    setSuggestedComps([]);
    try {
      const response = await compAPI.generate(id, 5);
      setSuggestedComps(response.data.suggested_comps || []);
      if (response.data.fallback) {
        toast.info('Generated estimated comps - please verify with MLS data');
      } else {
        toast.success(`Generated ${response.data.suggested_comps?.length || 0} suggested comps`);
      }
    } catch (error) {
      toast.error('Failed to generate comps');
    } finally {
      setCompsGenerating(false);
    }
  };

  const handleAddSuggestedComp = async (comp) => {
    try {
      await compAPI.create({
        project_id: id,
        ...comp,
        sale_price: parseFloat(comp.sale_price) || 0,
        beds: parseInt(comp.beds) || 0,
        baths: parseFloat(comp.baths) || 0,
        sqft: parseInt(comp.sqft) || 0
      });
      toast.success('Comp added');
      // Remove from suggestions
      setSuggestedComps(prev => prev.filter(c => c.address !== comp.address));
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to add comp');
    }
  };

  const handleEditSuggestedComp = (comp) => {
    setNewComp({
      address: comp.address || '',
      city: comp.city || '',
      state: comp.state || '',
      zipcode: comp.zipcode || '',
      sale_price: String(comp.sale_price || ''),
      sale_date: comp.sale_date || '',
      beds: String(comp.beds || ''),
      baths: String(comp.baths || ''),
      sqft: String(comp.sqft || ''),
      notes: comp.notes || ''
    });
    setEditingComp(comp);
    // Remove from suggestions
    setSuggestedComps(prev => prev.filter(c => c.address !== comp.address));
    setCompDialogOpen(true);
  };

  const handleDismissSuggestedComp = (comp) => {
    setSuggestedComps(prev => prev.filter(c => c.address !== comp.address));
  };

  const handlePhotoUpload = async () => {
    if (!selectedPhoto) {
      toast.error('Please select a photo');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', selectedPhoto);
      formData.append('project_id', id);
      formData.append('category', photoCategory);
      formData.append('description', photoDescription);
      
      await photoAPI.upload(formData);
      toast.success('Photo uploaded');
      setPhotoDialogOpen(false);
      setSelectedPhoto(null);
      setPhotoDescription('');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await photoAPI.delete(photoId);
      toast.success('Photo deleted');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await projectAPI.update(id, { status: newStatus });
      toast.success('Status updated');
      setEditStatusDialogOpen(false);
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Permit handlers
  const handleAddPermit = async () => {
    if (!newPermit.permit_type) {
      toast.error('Permit type is required');
      return;
    }
    try {
      await permitAPI.create({
        project_id: id,
        ...newPermit,
        fee: parseFloat(newPermit.fee) || 0
      });
      toast.success('Permit added');
      setPermitDialogOpen(false);
      setNewPermit({ permit_type: '', permit_number: '', status: 'pending', issue_date: '', expiration_date: '', fee: '', inspection_date: '', inspection_status: '', inspection_notes: '', notes: '' });
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to add permit');
    }
  };

  const handleDeletePermit = async (permitId) => {
    try {
      await permitAPI.delete(permitId);
      toast.success('Permit deleted');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete permit');
    }
  };

  // Contractor Portal handlers
  const handleCreatePortal = async () => {
    if (!newPortal.contractor_name) {
      toast.error('Contractor name is required');
      return;
    }
    try {
      const response = await contractorPortalAPI.create({
        project_id: id,
        ...newPortal
      });
      setCreatedPortal(response.data);
      toast.success('Portal created!');
      setNewPortal({ contractor_name: '', expires_in_days: 30 });
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to create portal');
    }
  };

  const handleTogglePortal = async (portalId) => {
    try {
      await contractorPortalAPI.toggle(portalId);
      toast.success('Portal status updated');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to update portal');
    }
  };

  const handleDeletePortal = async (portalId) => {
    try {
      await contractorPortalAPI.delete(portalId);
      toast.success('Portal deleted');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete portal');
    }
  };

  // Contractor Account handlers
  const handleCreateAccount = async () => {
    if (!newAccount.name || !newAccount.email) {
      toast.error('Name and email are required');
      return;
    }
    try {
      const response = await contractorAccountAPI.create({
        project_id: id,
        ...newAccount
      });
      setCreatedAccount(response.data);
      toast.success('Account created!');
      setNewAccount({ name: '', email: '', phone: '', company: '', specialty: '' });
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    }
  };

  const handleToggleAccount = async (accountId) => {
    try {
      await contractorAccountAPI.toggle(accountId);
      toast.success('Account status updated');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to update account');
    }
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      await contractorAccountAPI.delete(accountId);
      toast.success('Account deleted');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete account');
    }
  };

  const handleResetPassword = async (accountId) => {
    try {
      const response = await contractorAccountAPI.resetPassword(accountId);
      toast.success(`New password: ${response.data.temp_password}`);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getPortalUrl = (portalId, code) => {
    return `${window.location.origin}/contractor/upload/${portalId}?code=${code}`;
  };

  const handleGetAiInsights = async () => {
    setAiLoading(true);
    try {
      const response = await aiAPI.getInsights(id, aiQuestion || null);
      setAiInsights(response.data);
    } catch (error) {
      toast.error('Failed to get AI insights');
    } finally {
      setAiLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      toast.info('Generating PDF report...');
      const response = await reportAPI.getMarketAnalysisPdf(id);
      
      // Validate response
      if (!response.data || response.data.size === 0) {
        throw new Error('Empty PDF response');
      }
      
      // Create blob and use file-saver for reliable download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `DealVault_${project?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'report'}_Report.pdf`;
      
      // Use file-saver library for cross-browser reliable download
      saveAs(blob, filename);
      
      toast.success(`Report saved as "${filename}"! Check your Downloads folder.`);
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      planning: 'status-planning',
      'in-progress': 'status-in-progress',
      completed: 'status-completed',
      sold: 'status-sold',
    };
    return styles[status] || 'bg-secondary text-secondary-foreground';
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'priority-low',
      medium: 'priority-medium',
      high: 'priority-high',
    };
    return styles[priority] || 'bg-secondary';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold">Project not found</h2>
        <Link to="/projects" className="text-primary hover:underline mt-2 inline-block">
          Back to Projects
        </Link>
      </div>
    );
  }

  const EXPENSE_CATEGORIES = ['Material Cost', 'Materials', 'Labor', 'Permits', 'Utilities', 'Marketing', 'Closing Costs', 'Holding Costs', 'Appliances', 'Fixtures', 'Landscaping', 'Other'];

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/projects')}
            className="text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-sm bg-primary/10 flex items-center justify-center">
              <Building2 className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold font-heading tracking-tight">{project.name}</h1>
              <div className="flex items-center text-muted-foreground mt-1">
                <MapPin className="w-4 h-4 mr-1" />
                {project.address}, {project.city}, {project.state} {project.zipcode}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/reports">
            <Button 
              variant="outline" 
              className="rounded-sm"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Reports
            </Button>
          </Link>
          <Button 
            variant="outline" 
            onClick={handleDownloadPdf}
            className="rounded-sm"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            PDF Report
          </Button>
          <Dialog open={editStatusDialogOpen} onOpenChange={setEditStatusDialogOpen}>
            <DialogTrigger asChild>
              <Badge 
                variant="outline" 
                className={`rounded-sm font-mono text-sm uppercase cursor-pointer hover:opacity-80 ${getStatusBadge(project.status)}`}
                data-testid="project-status-badge"
              >
                {project.status}
                <Edit2 className="w-3 h-3 ml-2" />
              </Badge>
            </DialogTrigger>
            <DialogContent className="bg-card border-border rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-heading">Update Project Status</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="bg-input/50 border-border rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border rounded-sm">
                    <SelectItem value="planning">Planning</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleUpdateStatus} className="w-full bg-primary text-primary-foreground rounded-sm">
                  Update Status
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Purchase Price</p>
            <p className="text-xl font-bold font-mono mt-1">{formatCurrency(project.purchase_price)}</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Budget Used</p>
            <p className="text-xl font-bold font-mono mt-1">{formatCurrency(stats?.total_estimated || stats?.total_expenses || 0)}</p>
            <Progress value={stats?.budget_percentage || 0} className="h-1 mt-2" />
            <p className="text-xs text-muted-foreground mt-1">
              {formatCurrency(stats?.total_expenses || 0)} spent + {formatCurrency(stats?.estimated_remaining || 0)} estimated
            </p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Estimated ARV</p>
            <p className="text-xl font-bold font-mono mt-1 text-green-400">{formatCurrency(project.estimated_arv)}</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Task Progress</p>
            <p className="text-xl font-bold font-mono mt-1">{stats?.completed_tasks || 0}/{stats?.total_tasks || 0}</p>
            <Progress value={stats?.completion_percentage || 0} className="h-1 mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-secondary/50 border border-border rounded-sm p-1 flex-wrap">
          <TabsTrigger value="overview" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Overview</TabsTrigger>
          <TabsTrigger value="tasks" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Tasks</TabsTrigger>
          <TabsTrigger value="budget" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Budget</TabsTrigger>
          <TabsTrigger value="documents" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Documents</TabsTrigger>
          <TabsTrigger value="permits" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Permits</TabsTrigger>
          <TabsTrigger value="photos" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Photos</TabsTrigger>
          <TabsTrigger value="contractors" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Contractors</TabsTrigger>
          <TabsTrigger value="comps" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Comps</TabsTrigger>
          <TabsTrigger value="ai" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">AI Insights</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4" data-testid="tab-overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-border bg-card rounded-sm">
              <CardHeader>
                <CardTitle className="font-heading">Project Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Target Budget</p>
                    <p className="font-mono font-semibold">{formatCurrency(project.target_budget)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Budget Remaining</p>
                    <p className={`font-mono font-semibold ${(stats?.budget_remaining || 0) < 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {formatCurrency(stats?.budget_remaining)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Target Completion</p>
                    <p className="font-semibold">{formatDate(project.target_completion_date)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Created</p>
                    <p className="font-semibold">{formatDate(project.created_at)}</p>
                  </div>
                </div>
                {project.notes && (
                  <div className="pt-4 border-t border-border">
                    <p className="text-muted-foreground text-sm mb-2">Notes</p>
                    <p className="text-sm">{project.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border bg-card rounded-sm">
              <CardHeader>
                <CardTitle className="font-heading">Budget Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Budget Summary */}
                <div className="grid grid-cols-2 gap-3 p-3 bg-secondary/30 rounded-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Actual Spent</p>
                    <p className="font-mono font-semibold text-red-400">{formatCurrency(stats?.total_expenses || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Estimated (Pending Tasks)</p>
                    <p className="font-mono font-semibold text-amber-400">{formatCurrency(stats?.estimated_remaining || 0)}</p>
                  </div>
                </div>
                
                {/* Category Breakdown */}
                {stats?.expense_by_category && Object.keys(stats.expense_by_category).length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">By Category</p>
                    {Object.entries(stats.expense_by_category).map(([category, amount]) => (
                      <div key={category} className="flex items-center justify-between">
                        <span className="text-sm">{category}</span>
                        <span className="font-mono text-sm">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No expenses recorded yet. Complete tasks to log expenses automatically.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-4" data-testid="tab-tasks">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold font-heading">Contractor Tasks</h3>
            <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-primary-foreground rounded-sm btn-scale" data-testid="add-task-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Task
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add New Task</DialogTitle>
                  <DialogDescription>Track contractor work and deadlines</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Task Title *</Label>
                    <Input
                      value={newTask.title}
                      onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Kitchen Renovation"
                      className="bg-input/50 border-border rounded-sm"
                      data-testid="task-title-input"
                    />
                  </div>
                  <div>
                    <Label>Contractor Name</Label>
                    <Input
                      value={newTask.contractor_name}
                      onChange={(e) => setNewTask(prev => ({ ...prev, contractor_name: e.target.value }))}
                      placeholder="e.g., John's Contracting"
                      className="bg-input/50 border-border rounded-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Estimated Cost</Label>
                      <Input
                        type="number"
                        value={newTask.estimated_cost}
                        onChange={(e) => setNewTask(prev => ({ ...prev, estimated_cost: e.target.value }))}
                        placeholder="5000"
                        className="bg-input/50 border-border rounded-sm"
                      />
                    </div>
                    <div>
                      <Label>Due Date</Label>
                      <Input
                        type="date"
                        value={newTask.due_date}
                        onChange={(e) => setNewTask(prev => ({ ...prev, due_date: e.target.value }))}
                        className="bg-input/50 border-border rounded-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={newTask.priority} onValueChange={(value) => setNewTask(prev => ({ ...prev, priority: value }))}>
                      <SelectTrigger className="bg-input/50 border-border rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-border rounded-sm">
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={newTask.description}
                      onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Task details..."
                      className="bg-input/50 border-border rounded-sm"
                    />
                  </div>
                  <Button onClick={handleAddTask} className="w-full bg-primary text-primary-foreground rounded-sm" data-testid="save-task-btn">
                    Add Task
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {tasks.length > 0 ? (
            <div className="space-y-3">
              {tasks.map((task) => (
                <Card key={task.id} className="border-border bg-card rounded-sm" data-testid={`task-item-${task.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => handleUpdateTaskStatus(task.id, task.status === 'completed' ? 'pending' : 'completed')}
                          className={`mt-1 w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-colors ${
                            task.status === 'completed' 
                              ? 'bg-green-500 border-green-500' 
                              : 'border-muted-foreground hover:border-primary'
                          }`}
                        >
                          {task.status === 'completed' && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </button>
                        <div>
                          <h4 className={`font-semibold ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                            {task.title}
                          </h4>
                          {task.contractor_name && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <User className="w-3 h-3" />
                              {task.contractor_name}
                            </p>
                          )}
                          {task.description && (
                            <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2">
                            {task.due_date && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(task.due_date)}
                              </span>
                            )}
                            {task.estimated_cost > 0 && (
                              <span className="text-xs font-mono text-muted-foreground">
                                Est: {formatCurrency(task.estimated_cost)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`rounded-sm text-xs ${getPriorityBadge(task.priority)}`}>
                          {task.priority}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteTask(task.id)}
                          className="text-muted-foreground hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-border bg-card rounded-sm">
              <CardContent className="p-8 text-center">
                <Clock className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-semibold mb-2">No tasks yet</h3>
                <p className="text-sm text-muted-foreground">Add tasks to track contractor progress</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Budget Tab */}
        <TabsContent value="budget" className="space-y-4" data-testid="tab-budget">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold font-heading">Expenses</h3>
            <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-primary-foreground rounded-sm btn-scale" data-testid="add-expense-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add Expense</DialogTitle>
                  <DialogDescription>Track project expenses by category</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Category *</Label>
                    <Select value={newExpense.category} onValueChange={(value) => setNewExpense(prev => ({ ...prev, category: value }))}>
                      <SelectTrigger className="bg-input/50 border-border rounded-sm" data-testid="expense-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-border rounded-sm">
                        {EXPENSE_CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Amount *</Label>
                    <Input
                      type="number"
                      value={newExpense.amount}
                      onChange={(e) => setNewExpense(prev => ({ ...prev, amount: e.target.value }))}
                      placeholder="1500"
                      className="bg-input/50 border-border rounded-sm"
                      data-testid="expense-amount-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={newExpense.description}
                      onChange={(e) => setNewExpense(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="e.g., Kitchen cabinets"
                      className="bg-input/50 border-border rounded-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Vendor</Label>
                      <Input
                        value={newExpense.vendor}
                        onChange={(e) => setNewExpense(prev => ({ ...prev, vendor: e.target.value }))}
                        placeholder="e.g., Home Depot"
                        className="bg-input/50 border-border rounded-sm"
                      />
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={newExpense.date}
                        onChange={(e) => setNewExpense(prev => ({ ...prev, date: e.target.value }))}
                        className="bg-input/50 border-border rounded-sm"
                      />
                    </div>
                  </div>
                  <Button onClick={handleAddExpense} className="w-full bg-primary text-primary-foreground rounded-sm" data-testid="save-expense-btn">
                    Add Expense
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Budget Summary */}
          <Card className="border-border bg-card rounded-sm">
            <CardContent className="p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Total Budget</p>
                  <p className="text-xl font-bold font-mono">{formatCurrency(project.target_budget)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Spent</p>
                  <p className="text-xl font-bold font-mono text-amber-400">{formatCurrency(stats?.total_expenses)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Remaining</p>
                  <p className={`text-xl font-bold font-mono ${(stats?.budget_remaining || 0) < 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {formatCurrency(stats?.budget_remaining)}
                  </p>
                </div>
              </div>
              <Progress value={stats?.budget_percentage || 0} className="h-2 mt-4" />
              <p className="text-xs text-muted-foreground text-center mt-2">{stats?.budget_percentage}% of budget used</p>
            </CardContent>
          </Card>

          {expenses.length > 0 ? (
            <div className="space-y-2">
              {expenses.map((expense) => (
                <Card key={expense.id} className="border-border bg-card rounded-sm" data-testid={`expense-item-${expense.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="rounded-sm text-xs">{expense.category}</Badge>
                        <span className="font-semibold">{expense.description || 'No description'}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                        {expense.vendor && <span>{expense.vendor}</span>}
                        <span>{formatDate(expense.date)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold">{formatCurrency(expense.amount)}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteExpense(expense.id)}
                        className="text-muted-foreground hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-border bg-card rounded-sm">
              <CardContent className="p-8 text-center">
                <DollarSign className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-semibold mb-2">No expenses yet</h3>
                <p className="text-sm text-muted-foreground">Start tracking your project expenses</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4" data-testid="tab-documents">
          <Card className="border-border bg-card rounded-sm">
            <CardContent className="p-6">
              <DocumentVault projectId={id} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Photos Tab */}
        <TabsContent value="photos" className="space-y-4" data-testid="tab-photos">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold font-heading">Photo Documentation</h3>
            <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-primary-foreground rounded-sm btn-scale" data-testid="add-photo-btn">
                  <Camera className="w-4 h-4 mr-2" />
                  Upload Photo
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-heading">Upload Photo</DialogTitle>
                  <DialogDescription>Document before/after progress</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Category</Label>
                    <Select value={photoCategory} onValueChange={setPhotoCategory}>
                      <SelectTrigger className="bg-input/50 border-border rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-border rounded-sm">
                        <SelectItem value="before">Before</SelectItem>
                        <SelectItem value="during">During</SelectItem>
                        <SelectItem value="after">After</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Photo</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setSelectedPhoto(e.target.files[0])}
                      className="bg-input/50 border-border rounded-sm"
                      data-testid="photo-file-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={photoDescription}
                      onChange={(e) => setPhotoDescription(e.target.value)}
                      placeholder="e.g., Kitchen - Day 1"
                      className="bg-input/50 border-border rounded-sm"
                    />
                  </div>
                  <Button onClick={handlePhotoUpload} className="w-full bg-primary text-primary-foreground rounded-sm" data-testid="upload-photo-btn">
                    Upload
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <Card key={photo.id} className="border-border bg-card rounded-sm overflow-hidden group" data-testid={`photo-item-${photo.id}`}>
                  <div className="aspect-square bg-secondary flex items-center justify-center relative">
                    <Camera className="w-8 h-8 text-muted-foreground/30" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeletePhoto(photo.id)}
                        className="text-white hover:text-red-400"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <Badge variant="outline" className="rounded-sm text-xs mb-1">{photo.category}</Badge>
                    <p className="text-sm truncate">{photo.description || photo.filename}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(photo.created_at)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-border bg-card rounded-sm">
              <CardContent className="p-8 text-center">
                <Camera className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-semibold mb-2">No photos yet</h3>
                <p className="text-sm text-muted-foreground">Upload before/after photos to document progress</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Permits Tab */}
        <TabsContent value="permits" className="space-y-4" data-testid="tab-permits">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold font-heading">Permits & Inspections</h3>
              <p className="text-sm text-muted-foreground">Track permits, fees, and inspection schedules</p>
            </div>
            <Dialog open={permitDialogOpen} onOpenChange={setPermitDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-primary-foreground rounded-sm btn-scale" data-testid="add-permit-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Permit
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border rounded-sm max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add Permit</DialogTitle>
                  <DialogDescription>Track permit status and inspections</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Permit Type *</Label>
                      <Select value={newPermit.permit_type} onValueChange={(value) => setNewPermit(prev => ({ ...prev, permit_type: value }))}>
                        <SelectTrigger className="bg-input/50 border-border rounded-sm">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border rounded-sm">
                          <SelectItem value="building">Building</SelectItem>
                          <SelectItem value="electrical">Electrical</SelectItem>
                          <SelectItem value="plumbing">Plumbing</SelectItem>
                          <SelectItem value="mechanical">Mechanical/HVAC</SelectItem>
                          <SelectItem value="roofing">Roofing</SelectItem>
                          <SelectItem value="demolition">Demolition</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Permit Number</Label>
                      <Input
                        value={newPermit.permit_number}
                        onChange={(e) => setNewPermit(prev => ({ ...prev, permit_number: e.target.value }))}
                        placeholder="e.g., BP-2024-001"
                        className="bg-input/50 border-border rounded-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Status</Label>
                      <Select value={newPermit.status} onValueChange={(value) => setNewPermit(prev => ({ ...prev, status: value }))}>
                        <SelectTrigger className="bg-input/50 border-border rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border rounded-sm">
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="failed">Failed/Rejected</SelectItem>
                          <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Fee ($)</Label>
                      <Input
                        type="number"
                        value={newPermit.fee}
                        onChange={(e) => setNewPermit(prev => ({ ...prev, fee: e.target.value }))}
                        placeholder="250"
                        className="bg-input/50 border-border rounded-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Issue Date</Label>
                      <Input
                        type="date"
                        value={newPermit.issue_date}
                        onChange={(e) => setNewPermit(prev => ({ ...prev, issue_date: e.target.value }))}
                        className="bg-input/50 border-border rounded-sm"
                      />
                    </div>
                    <div>
                      <Label>Expiration Date</Label>
                      <Input
                        type="date"
                        value={newPermit.expiration_date}
                        onChange={(e) => setNewPermit(prev => ({ ...prev, expiration_date: e.target.value }))}
                        className="bg-input/50 border-border rounded-sm"
                      />
                    </div>
                  </div>
                  <div className="border-t border-border pt-4">
                    <h4 className="font-semibold mb-3">Inspection Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Inspection Date</Label>
                        <Input
                          type="date"
                          value={newPermit.inspection_date}
                          onChange={(e) => setNewPermit(prev => ({ ...prev, inspection_date: e.target.value }))}
                          className="bg-input/50 border-border rounded-sm"
                        />
                      </div>
                      <div>
                        <Label>Inspection Status</Label>
                        <Select value={newPermit.inspection_status} onValueChange={(value) => setNewPermit(prev => ({ ...prev, inspection_status: value }))}>
                          <SelectTrigger className="bg-input/50 border-border rounded-sm">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent className="bg-popover border-border rounded-sm">
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="passed">Passed</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mt-4">
                      <Label>Inspection Notes</Label>
                      <Textarea
                        value={newPermit.inspection_notes}
                        onChange={(e) => setNewPermit(prev => ({ ...prev, inspection_notes: e.target.value }))}
                        placeholder="Inspector comments, required corrections, etc."
                        className="bg-input/50 border-border rounded-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={newPermit.notes}
                      onChange={(e) => setNewPermit(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Additional permit notes..."
                      className="bg-input/50 border-border rounded-sm"
                    />
                  </div>
                  <Button onClick={handleAddPermit} className="w-full bg-primary text-primary-foreground rounded-sm" data-testid="save-permit-btn">
                    Add Permit
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Permits Summary */}
          {permits.length > 0 && (
            <Card className="border-border bg-card rounded-sm">
              <CardContent className="p-4">
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Total Permits</p>
                    <p className="text-xl font-bold font-mono">{permits.length}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Approved</p>
                    <p className="text-xl font-bold font-mono text-green-400">{permits.filter(p => p.status === 'approved').length}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Pending</p>
                    <p className="text-xl font-bold font-mono text-amber-400">{permits.filter(p => ['pending', 'submitted'].includes(p.status)).length}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Total Fees</p>
                    <p className="text-xl font-bold font-mono">{formatCurrency(permits.reduce((sum, p) => sum + (p.fee || 0), 0))}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {permits.length > 0 ? (
            <div className="space-y-3">
              {permits.map((permit) => (
                <Card key={permit.id} className="border-border bg-card rounded-sm" data-testid={`permit-item-${permit.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-primary" />
                          <h4 className="font-semibold capitalize">{permit.permit_type} Permit</h4>
                          <Badge variant="outline" className={`rounded-sm text-xs ${
                            permit.status === 'approved' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                            permit.status === 'failed' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                            permit.status === 'expired' ? 'bg-gray-500/20 text-gray-400 border-gray-500/30' :
                            'bg-amber-500/20 text-amber-400 border-amber-500/30'
                          }`}>
                            {permit.status}
                          </Badge>
                        </div>
                        {permit.permit_number && (
                          <p className="text-sm text-muted-foreground font-mono">#{permit.permit_number}</p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {permit.issue_date && <span>Issued: {formatDate(permit.issue_date)}</span>}
                          {permit.expiration_date && <span>Expires: {formatDate(permit.expiration_date)}</span>}
                          {permit.fee > 0 && <span className="font-mono">{formatCurrency(permit.fee)}</span>}
                        </div>
                        {permit.inspection_date && (
                          <div className="mt-2 p-2 bg-secondary/50 rounded-sm">
                            <p className="text-xs text-muted-foreground uppercase mb-1">Inspection</p>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-3 h-3" />
                              <span className="text-sm">{formatDate(permit.inspection_date)}</span>
                              {permit.inspection_status && (
                                <Badge variant="outline" className={`rounded-sm text-xs ${
                                  permit.inspection_status === 'passed' ? 'bg-green-500/20 text-green-400' :
                                  permit.inspection_status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                  'bg-blue-500/20 text-blue-400'
                                }`}>
                                  {permit.inspection_status}
                                </Badge>
                              )}
                            </div>
                            {permit.inspection_notes && (
                              <p className="text-xs text-muted-foreground mt-1">{permit.inspection_notes}</p>
                            )}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeletePermit(permit.id)}
                        className="text-muted-foreground hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-border bg-card rounded-sm">
              <CardContent className="p-8 text-center">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-semibold mb-2">No permits yet</h3>
                <p className="text-sm text-muted-foreground">Add permits to track approval status and inspections</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Contractors Tab */}
        <TabsContent value="contractors" className="space-y-6" data-testid="tab-contractors">
          {/* Contractor Uploads Section */}
          {contractorUploads.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold font-heading">Contractor Uploads</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {contractorUploads.slice(0, 8).map((upload) => (
                  <Card key={upload.id} className="border-border bg-card rounded-sm">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2 mb-2">
                        {upload.upload_type === 'photo' ? <Camera className="w-4 h-4 text-blue-400" /> :
                         upload.upload_type === 'receipt' ? <FileText className="w-4 h-4 text-green-400" /> :
                         <FileText className="w-4 h-4 text-amber-400" />}
                        <Badge variant="outline" className="rounded-sm text-xs">{upload.upload_type}</Badge>
                      </div>
                      <p className="text-sm truncate">{upload.filename}</p>
                      <p className="text-xs text-muted-foreground">{upload.contractor_name}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(upload.created_at)}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Portal Links Section */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold font-heading">Portal Links</h3>
                <p className="text-sm text-muted-foreground">Shareable links for contractors to upload documents</p>
              </div>
              <Dialog open={portalDialogOpen} onOpenChange={(open) => { setPortalDialogOpen(open); if (!open) setCreatedPortal(null); }}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-primary-foreground rounded-sm btn-scale" data-testid="create-portal-btn">
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Create Portal Link
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border rounded-sm">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Create Contractor Portal</DialogTitle>
                    <DialogDescription>Generate a shareable link with access code</DialogDescription>
                  </DialogHeader>
                  {createdPortal ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                        <p className="text-sm text-green-400 font-semibold mb-2">Portal Created!</p>
                        <div className="space-y-2">
                          <div>
                            <Label className="text-xs">Contractor</Label>
                            <p className="font-semibold">{createdPortal.contractor_name}</p>
                          </div>
                          <div>
                            <Label className="text-xs">Access Code</Label>
                            <div className="flex items-center gap-2">
                              <code className="bg-secondary px-2 py-1 rounded text-lg font-mono">{createdPortal.access_code}</code>
                              <Button variant="ghost" size="icon" onClick={() => copyToClipboard(createdPortal.access_code)}>
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Portal Link</Label>
                            <div className="flex items-center gap-2">
                              <Input value={getPortalUrl(createdPortal.id, createdPortal.access_code)} readOnly className="bg-secondary text-xs font-mono" />
                              <Button variant="ghost" size="icon" onClick={() => copyToClipboard(getPortalUrl(createdPortal.id, createdPortal.access_code))}>
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <Button onClick={() => { setPortalDialogOpen(false); setCreatedPortal(null); }} className="w-full">Done</Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label>Contractor Name *</Label>
                        <Input
                          value={newPortal.contractor_name}
                          onChange={(e) => setNewPortal(prev => ({ ...prev, contractor_name: e.target.value }))}
                          placeholder="e.g., John's Electric"
                          className="bg-input/50 border-border rounded-sm"
                        />
                      </div>
                      <div>
                        <Label>Link Expires In</Label>
                        <Select value={String(newPortal.expires_in_days)} onValueChange={(value) => setNewPortal(prev => ({ ...prev, expires_in_days: parseInt(value) }))}>
                          <SelectTrigger className="bg-input/50 border-border rounded-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-popover border-border rounded-sm">
                            <SelectItem value="7">7 days</SelectItem>
                            <SelectItem value="14">14 days</SelectItem>
                            <SelectItem value="30">30 days</SelectItem>
                            <SelectItem value="60">60 days</SelectItem>
                            <SelectItem value="90">90 days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={handleCreatePortal} className="w-full bg-primary text-primary-foreground rounded-sm">
                        Generate Portal Link
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>

            {portals.length > 0 ? (
              <div className="space-y-2">
                {portals.map((portal) => (
                  <Card key={portal.id} className="border-border bg-card rounded-sm">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${portal.is_active ? 'bg-green-500/10' : 'bg-gray-500/10'}`}>
                          <LinkIcon className={`w-5 h-5 ${portal.is_active ? 'text-green-400' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <p className="font-semibold">{portal.contractor_name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>Code: <code className="font-mono">{portal.access_code}</code></span>
                            <span>•</span>
                            <span>{portal.upload_count} uploads</span>
                            <span>•</span>
                            <span>Expires: {formatDate(portal.expires_at)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => copyToClipboard(getPortalUrl(portal.id, portal.access_code))}>
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleTogglePortal(portal.id)}>
                          {portal.is_active ? <Eye className="w-4 h-4 text-green-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeletePortal(portal.id)} className="text-muted-foreground hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-border bg-card rounded-sm">
                <CardContent className="p-6 text-center">
                  <LinkIcon className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground">No portal links created yet</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Contractor Accounts Section */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold font-heading">Contractor Accounts</h3>
                <p className="text-sm text-muted-foreground">Contractors with login access to upload documents</p>
              </div>
              <Dialog open={accountDialogOpen} onOpenChange={(open) => { setAccountDialogOpen(open); if (!open) setCreatedAccount(null); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-sm" data-testid="create-account-btn">
                    <HardHat className="w-4 h-4 mr-2" />
                    Add Contractor
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border rounded-sm">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Create Contractor Account</DialogTitle>
                    <DialogDescription>Contractor will receive login credentials</DialogDescription>
                  </DialogHeader>
                  {createdAccount ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                        <p className="text-sm text-green-400 font-semibold mb-2">Account Created!</p>
                        <div className="space-y-2">
                          <div>
                            <Label className="text-xs">Name</Label>
                            <p className="font-semibold">{createdAccount.name}</p>
                          </div>
                          <div>
                            <Label className="text-xs">Email</Label>
                            <p className="font-mono">{createdAccount.email}</p>
                          </div>
                          <div>
                            <Label className="text-xs">Temporary Password</Label>
                            <div className="flex items-center gap-2">
                              <code className="bg-secondary px-2 py-1 rounded font-mono">{createdAccount.temp_password}</code>
                              <Button variant="ghost" size="icon" onClick={() => copyToClipboard(createdAccount.temp_password)}>
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Project ID (for login)</Label>
                            <div className="flex items-center gap-2">
                              <code className="bg-secondary px-2 py-1 rounded font-mono text-xs">{id}</code>
                              <Button variant="ghost" size="icon" onClick={() => copyToClipboard(id)}>
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-3">Share these credentials with the contractor</p>
                      </div>
                      <Button onClick={() => { setAccountDialogOpen(false); setCreatedAccount(null); }} className="w-full">Done</Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          value={newAccount.name}
                          onChange={(e) => setNewAccount(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="John Smith"
                          className="bg-input/50 border-border rounded-sm"
                        />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={newAccount.email}
                          onChange={(e) => setNewAccount(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="contractor@example.com"
                          className="bg-input/50 border-border rounded-sm"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Phone</Label>
                          <Input
                            value={newAccount.phone}
                            onChange={(e) => setNewAccount(prev => ({ ...prev, phone: e.target.value }))}
                            placeholder="(555) 123-4567"
                            className="bg-input/50 border-border rounded-sm"
                          />
                        </div>
                        <div>
                          <Label>Company</Label>
                          <Input
                            value={newAccount.company}
                            onChange={(e) => setNewAccount(prev => ({ ...prev, company: e.target.value }))}
                            placeholder="Smith Electric"
                            className="bg-input/50 border-border rounded-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Specialty</Label>
                        <Select value={newAccount.specialty} onValueChange={(value) => setNewAccount(prev => ({ ...prev, specialty: value }))}>
                          <SelectTrigger className="bg-input/50 border-border rounded-sm">
                            <SelectValue placeholder="Select specialty" />
                          </SelectTrigger>
                          <SelectContent className="bg-popover border-border rounded-sm">
                            <SelectItem value="general">General Contractor</SelectItem>
                            <SelectItem value="electrical">Electrical</SelectItem>
                            <SelectItem value="plumbing">Plumbing</SelectItem>
                            <SelectItem value="hvac">HVAC</SelectItem>
                            <SelectItem value="roofing">Roofing</SelectItem>
                            <SelectItem value="flooring">Flooring</SelectItem>
                            <SelectItem value="painting">Painting</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={handleCreateAccount} className="w-full bg-primary text-primary-foreground rounded-sm">
                        Create Account
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>

            {contractorAccounts.length > 0 ? (
              <div className="space-y-2">
                {contractorAccounts.map((account) => (
                  <Card key={account.id} className="border-border bg-card rounded-sm">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${account.is_active ? 'bg-amber-500/10' : 'bg-gray-500/10'}`}>
                          <HardHat className={`w-5 h-5 ${account.is_active ? 'text-amber-500' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <p className="font-semibold">{account.name}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{account.email}</span>
                            {account.company && <span>{account.company}</span>}
                            {account.specialty && <Badge variant="outline" className="rounded-sm text-xs">{account.specialty}</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {account.upload_count} uploads • Last login: {account.last_login ? formatDate(account.last_login) : 'Never'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleResetPassword(account.id)} title="Reset Password">
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleToggleAccount(account.id)}>
                          {account.is_active ? <Eye className="w-4 h-4 text-green-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteAccount(account.id)} className="text-muted-foreground hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-border bg-card rounded-sm">
                <CardContent className="p-6 text-center">
                  <HardHat className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground">No contractor accounts created yet</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Comps Tab */}
        <TabsContent value="comps" className="space-y-4" data-testid="tab-comps">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <h3 className="text-lg font-bold font-heading">Comparable Properties</h3>
              <p className="text-sm text-muted-foreground">Recent sales in the area (last 3 months)</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleGenerateComps}
                disabled={compsGenerating}
                className="rounded-sm"
                data-testid="generate-comps-btn"
              >
                {compsGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Brain className="w-4 h-4 mr-2" />
                    AI Generate Comps
                  </>
                )}
              </Button>
              <Dialog open={compDialogOpen} onOpenChange={(open) => { setCompDialogOpen(open); if (!open) { setEditingComp(null); setNewComp({ address: '', city: '', state: '', zipcode: '', sale_price: '', sale_date: '', beds: '', baths: '', sqft: '', notes: '' }); } }}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-primary-foreground rounded-sm btn-scale" data-testid="add-comp-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Manually
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border rounded-sm max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading">{editingComp ? 'Edit & Add Comp' : 'Add Comparable Property'}</DialogTitle>
                    <DialogDescription>{editingComp ? 'Review and modify the suggested comp before adding' : 'Enter details of a recently sold property in the area'}</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                    <div>
                      <Label>Address *</Label>
                      <Input
                        value={newComp.address}
                        onChange={(e) => setNewComp(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="123 Elm Street"
                        className="bg-input/50 border-border rounded-sm"
                        data-testid="comp-address-input"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>City</Label>
                        <Input
                          value={newComp.city}
                          onChange={(e) => setNewComp(prev => ({ ...prev, city: e.target.value }))}
                          placeholder="Austin"
                          className="bg-input/50 border-border rounded-sm"
                        />
                      </div>
                      <div>
                        <Label>State</Label>
                        <Input
                          value={newComp.state}
                          onChange={(e) => setNewComp(prev => ({ ...prev, state: e.target.value }))}
                          placeholder="TX"
                          maxLength={2}
                          className="bg-input/50 border-border rounded-sm"
                        />
                      </div>
                      <div>
                        <Label>Zip</Label>
                        <Input
                          value={newComp.zipcode}
                          onChange={(e) => setNewComp(prev => ({ ...prev, zipcode: e.target.value }))}
                          placeholder="78701"
                          className="bg-input/50 border-border rounded-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Sale Price *</Label>
                        <Input
                        type="number"
                        value={newComp.sale_price}
                        onChange={(e) => setNewComp(prev => ({ ...prev, sale_price: e.target.value }))}
                        placeholder="350000"
                        className="bg-input/50 border-border rounded-sm"
                        data-testid="comp-price-input"
                      />
                    </div>
                    <div>
                      <Label>Sale Date *</Label>
                      <Input
                        type="date"
                        value={newComp.sale_date}
                        onChange={(e) => setNewComp(prev => ({ ...prev, sale_date: e.target.value }))}
                        className="bg-input/50 border-border rounded-sm"
                        data-testid="comp-date-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Beds</Label>
                      <Input
                        type="number"
                        value={newComp.beds}
                        onChange={(e) => setNewComp(prev => ({ ...prev, beds: e.target.value }))}
                        placeholder="3"
                        className="bg-input/50 border-border rounded-sm"
                      />
                    </div>
                    <div>
                      <Label>Baths</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={newComp.baths}
                        onChange={(e) => setNewComp(prev => ({ ...prev, baths: e.target.value }))}
                        placeholder="2"
                        className="bg-input/50 border-border rounded-sm"
                      />
                    </div>
                    <div>
                      <Label>Sqft</Label>
                      <Input
                        type="number"
                        value={newComp.sqft}
                        onChange={(e) => setNewComp(prev => ({ ...prev, sqft: e.target.value }))}
                        placeholder="1800"
                        className="bg-input/50 border-border rounded-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={newComp.notes}
                      onChange={(e) => setNewComp(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any notes about this comp..."
                      className="bg-input/50 border-border rounded-sm"
                    />
                  </div>
                  <Button onClick={handleAddComp} className="w-full bg-primary text-primary-foreground rounded-sm" data-testid="save-comp-btn">
                  Add Comp
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Comps Summary */}
          {comps.length > 0 && (
            <Card className="border-border bg-card rounded-sm">
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Comps Count</p>
                    <p className="text-xl font-bold font-mono">{comps.length}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Avg Price</p>
                    <p className="text-xl font-bold font-mono text-green-400">
                      {formatCurrency(comps.reduce((sum, c) => sum + c.sale_price, 0) / comps.length)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Price Range</p>
                    <p className="text-sm font-mono">
                      {formatCurrency(Math.min(...comps.map(c => c.sale_price)))} - {formatCurrency(Math.max(...comps.map(c => c.sale_price)))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Suggested Comps */}
          {suggestedComps.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-primary" />
                <h4 className="font-semibold">AI Suggested Comps</h4>
                <Badge variant="outline" className="rounded-sm text-xs bg-primary/10 text-primary border-primary/30">
                  {suggestedComps.length} suggestions
                </Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {suggestedComps.map((comp, index) => (
                  <Card key={index} className="border-border bg-card rounded-sm border-dashed border-primary/30" data-testid={`suggested-comp-${index}`}>
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="font-semibold text-sm">{comp.address}</h5>
                            <p className="text-xs text-muted-foreground">{comp.city}, {comp.state} {comp.zipcode}</p>
                          </div>
                          <p className="font-mono font-bold text-green-400">{formatCurrency(comp.sale_price)}</p>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{comp.beds} bed</span>
                          <span>{comp.baths} bath</span>
                          <span>{comp.sqft?.toLocaleString()} sqft</span>
                          <span>Sold: {formatDate(comp.sale_date)}</span>
                        </div>
                        {comp.notes && <p className="text-xs text-muted-foreground italic">{comp.notes}</p>}
                        <div className="flex gap-2 pt-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleAddSuggestedComp(comp)}
                            className="flex-1 bg-primary text-primary-foreground rounded-sm h-8"
                            data-testid={`add-suggested-comp-${index}`}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEditSuggestedComp(comp)}
                            className="flex-1 rounded-sm h-8"
                            data-testid={`edit-suggested-comp-${index}`}
                          >
                            <Edit2 className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleDismissSuggestedComp(comp)}
                            className="rounded-sm h-8 text-muted-foreground hover:text-red-400"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {comps.length > 0 ? (
            <div className="space-y-3">
              {comps.map((comp) => (
                <Card key={comp.id} className="border-border bg-card rounded-sm" data-testid={`comp-item-${comp.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold">{comp.address}</h4>
                        <p className="text-sm text-muted-foreground">
                          {comp.city}, {comp.state} {comp.zipcode}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span>{comp.beds} bed</span>
                          <span>{comp.baths} bath</span>
                          <span>{comp.sqft?.toLocaleString()} sqft</span>
                          <span className="text-muted-foreground">Sold: {formatDate(comp.sale_date)}</span>
                        </div>
                        {comp.notes && <p className="text-sm text-muted-foreground mt-2">{comp.notes}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold font-mono text-green-400">{formatCurrency(comp.sale_price)}</p>
                        <p className="text-xs text-muted-foreground">
                          ${Math.round(comp.sale_price / (comp.sqft || 1))}/sqft
                        </p>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteComp(comp.id)}
                          className="text-muted-foreground hover:text-red-400 mt-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-border bg-card rounded-sm">
              <CardContent className="p-8 text-center">
                <TrendingUp className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-semibold mb-2">No comps yet</h3>
                <p className="text-sm text-muted-foreground">Add comparable properties to analyze the market</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="ai" className="space-y-4" data-testid="tab-ai">
          <Card className="border-border bg-card rounded-sm amber-glow">
            <CardHeader className="border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-heading">AI-Powered Insights</CardTitle>
                  <CardDescription>Get budget predictions, timeline estimates & investment recommendations</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <Label>Ask a specific question (optional)</Label>
                  <Textarea
                    value={aiQuestion}
                    onChange={(e) => setAiQuestion(e.target.value)}
                    placeholder="e.g., What improvements would give me the best ROI?"
                    className="bg-input/50 border-border rounded-sm"
                    data-testid="ai-question-input"
                  />
                </div>
                <Button 
                  onClick={handleGetAiInsights}
                  disabled={aiLoading}
                  className="w-full bg-primary text-primary-foreground rounded-sm btn-scale"
                  data-testid="get-ai-insights-btn"
                >
                  {aiLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Brain className="w-4 h-4 mr-2" />
                      Get AI Insights
                    </>
                  )}
                </Button>
              </div>

              {aiInsights && (
                <div className="mt-6 p-4 bg-secondary/50 rounded-sm border border-border">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Brain className="w-4 h-4 text-primary" />
                    Analysis Results
                    {aiInsights.fallback && (
                      <Badge variant="outline" className="rounded-sm text-xs">Basic Analysis</Badge>
                    )}
                  </h4>
                  <div className="prose prose-invert prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap font-body text-sm text-foreground bg-transparent p-0">
                      {aiInsights.insights}
                    </pre>
                  </div>
                  {aiInsights.context && (
                    <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                      <div>
                        <p className="text-muted-foreground">Total Spent</p>
                        <p className="font-mono font-semibold">{formatCurrency(aiInsights.context.total_spent)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Budget Remaining</p>
                        <p className="font-mono font-semibold">{formatCurrency(aiInsights.context.budget_remaining)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Completion</p>
                        <p className="font-mono font-semibold">{aiInsights.context.completion_percentage}%</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Avg Comp Price</p>
                        <p className="font-mono font-semibold">{formatCurrency(aiInsights.context.avg_comp_price)}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProjectDetailPage;
